import { createContext, useContext } from 'react';

/**
 * Opt-in immersive (full-screen) mode for a page that wants to own the whole screen — today the Advanced
 * Video storyboard editor. When on, MainLayout slides the side menu out (a back button restores it) so the
 * page content fills the width. State lives in MainLayout; a page toggles it via `setImmersive`, passing an
 * `onExit` callback the layout's back button runs (e.g. to leave the immersive view). Default off / inert,
 * so pages and Playwright CTs mount standalone with no provider and nothing changes for any other page.
 */
/** A name editor the immersive page hands to the shared chrome; the top bar renders it in the breadcrumb
 *  slot while immersive (e.g. the video ad's name), so the label lives in the chrome but the value is owned
 *  by the page. */
export interface TitleEditor {
  value: string;
  onChange: (value: string) => void;
}

export interface ImmersiveControl {
  immersive: boolean;
  /**
   * Turn immersive mode on/off. When turning ON, `onExit` is what the layout's back button invokes on
   * exit (in addition to leaving immersive) — typically "navigate the page out of the immersive view".
   */
  setImmersive: (on: boolean, onExit?: () => void) => void;
  /**
   * Leave immersive mode: turns it off AND runs the `onExit` handed to the last `setImmersive(true, …)`.
   * The back button (rendered in the top bar's breadcrumb while immersive) calls this, so the trigger can
   * live in the shared chrome while the exit logic stays owned by MainLayout.
   */
  exitImmersive: () => void;
  /** An optional name editor rendered in the top bar's breadcrumb slot while immersive; null shows the normal
   *  breadcrumb. Set by the immersive page (cleared on exit). */
  titleEditor: TitleEditor | null;
  setTitleEditor: (editor: TitleEditor | null) => void;
}

const noop = (): void => undefined;

export const ImmersiveControlContext = createContext<ImmersiveControl>({
  immersive: false,
  setImmersive: noop,
  exitImmersive: noop,
  titleEditor: null,
  setTitleEditor: noop,
});

/** Access immersive mode (toggle full-screen chrome) from under MainLayout. */
export const useImmersive = (): ImmersiveControl => useContext(ImmersiveControlContext);
